import { Component, inject, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import {
    AbstractControl,
    FormBuilder,
    FormGroup,
    ValidationErrors,
    Validators,
} from "@angular/forms";
import { HeaderService } from "@app/components/header/header.service";
import { formatUsPhone, isValidUsPhone, phoneDigits } from "@app/util/phone";
import {
    HomeownerProfile,
    SsoHomeownerApiService,
} from "@app/services/sso/sso-homeowner-api.service";
import { AuthTokenService } from "@app/services/auth-token.service";

const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

function emailOrPhoneValidator(control: AbstractControl): ValidationErrors | null {
    const raw = (control.value ?? "").toString().trim();
    if (!raw) {
        return null;
    }
    const ok = EMAIL_REGEX.test(raw) || isValidUsPhone(raw);
    return ok ? null : { emailOrPhone: true };
}

@Component({
    selector: "app-search-sso-profile",
    template: require("./search-sso-profile.component.html"),
})
export class SearchSsoProfileComponent implements OnInit {
    private fb = inject(FormBuilder);
    private router = inject(Router);
    private auth = inject(AuthTokenService);
    private api = inject(SsoHomeownerApiService);
    private headerService = inject(HeaderService);

    form: FormGroup = this.fb.group({
        searchText: ["", [Validators.required, emailOrPhoneValidator]],
    });

    query = "";
    searched = false;
    searching = false;
    results: HomeownerProfile[] = [];

    ngOnInit(): void {
        this.headerService.setDisplayRightContent(false);
    }

    // Show the field error only after the user has interacted with it.
    showError(): boolean {
        const control = this.form.get("searchText")!;
        return control.invalid && control.touched;
    }

    // Normalize phone number to (xxx) xxx-xxxx when user leave the input field
    formatOnBlur(): void {
        const control = this.form.get("searchText")!;
        const raw = String(control.value ?? "");
        if (isValidUsPhone(raw)) {
            control.setValue(formatUsPhone(phoneDigits(raw)));
        }
    }

    search(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        const raw = String(this.form.value.searchText).trim();
        this.query = raw;
        // Backend takes either email OR phone; send digits when it's a phone.
        const criteria = EMAIL_REGEX.test(raw)
            ? { email: raw }
            : { phone: phoneDigits(raw) };

        this.searching = true;
        this.searched = false;
        this.api.search(this.auth.getSsoIdentity().userId, criteria).subscribe({
            next: (results) => {
                this.results = results;
                this.searched = true;
            },
            error: () => {
                this.searching = false;
            },
            complete: () => {
                this.searching = false;
            },
        });
    }

    // Selecting a match sends a one-time passcode to that profile's email
    // Only advance to the verify screen once the code has been sent.
    selectProfile(profile: HomeownerProfile): void {
        const userId = this.auth.getSsoIdentity().userId;
        this.api.sendChallenge(userId, profile.id).subscribe(() => {
            this.auth.setSsoSelectedProfile(profile.id, profile.email);
            this.router.navigate(["/sso/merge-profile"]);
        });
    }

    goBack(): void {
        this.router.navigate(["/sso/make-decision"]);
    }
}

